import { useCallback, useState } from 'react';
import { QueryFilters, useDownloadUniversalMfaMetricsFile } from '../api';
import { showAlert } from '@silverfort/sf-components';
import { downloadFile } from '../../../components/utils';
import { toJpeg } from 'html-to-image';
import { formatDate, DateFormat } from '../../../utils/dates';

const BOTTOM_PADDING_OFFSET = 16;
const CAPTURE_PADDING = 32;

interface Params {
    filters: QueryFilters;
    captureContainerRef: React.RefObject<HTMLDivElement>;
    captureFixedHeaderContainerRef: React.RefObject<HTMLDivElement>;
    captureScrollContainerRef: React.RefObject<HTMLDivElement>;
}

const useUniversalMfaDashboardToolbarActions = ({
    filters,
    captureContainerRef,
    captureFixedHeaderContainerRef,
    captureScrollContainerRef
}: Params) => {
    const [captureLoading, setCaptureLoading] = useState(false);

    const { generateAndDownloadUniversalMfaMetricsFile, loading: isGenerateAndDownloadUniversalMfaMetricsFileLoading } =
        useDownloadUniversalMfaMetricsFile({
            filters,
            onSuccess: useCallback(() => {
                showAlert('File was downloaded successfully', 'success', { title: 'Export successful' });
            }, []),
            onError: useCallback(() => {
                showAlert('Failed to download file', 'error', { title: 'Export failed' });
            }, [])
        });

    const handleCapture = useCallback(async (excludedClasses: string[] = []) => {
        if (
            captureContainerRef.current &&
            captureFixedHeaderContainerRef.current &&
            captureScrollContainerRef.current
        ) {
            setCaptureLoading(true);
            const { captureWidth, captureHeight, captureTopPadding, captureSidePadding, captureBottomPadding } =
                getCaptureDimensions(
                    captureContainerRef.current,
                    captureFixedHeaderContainerRef.current,
                    captureScrollContainerRef.current
                );

            try {
                const dataUrl = await toJpeg(captureContainerRef.current, {
                    width: captureWidth,
                    height: captureHeight,
                    backgroundColor: '#F6F6F6',
                    style: {
                        padding: `${captureTopPadding}px ${captureSidePadding}px ${captureBottomPadding}px`
                    },
                    filter:
                        excludedClasses.length > 0
                            ? (node) => !excludedClasses.some((className) => node.classList?.contains(className))
                            : undefined
                });

                downloadFile(
                    dataUrl,
                    `silverfort_universal_mfa_dashboard_capture_${formatDate(new Date(), DateFormat.FILE)}.jpeg`
                );
                setCaptureLoading(false);
                showAlert('Image was downloaded successfully', 'success', { title: 'Capture successful' });
            } catch (error) {
                setCaptureLoading(false);
                showAlert('Failed to download image', 'error', { title: 'Capture failed' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        generateAndDownloadUniversalMfaMetricsFile,
        isGenerateAndDownloadUniversalMfaMetricsFileLoading,
        generateAndDownloadUniversalMfaCapture: handleCapture,
        isGenerateAndDownloadUniversalMfaCaptureLoading: captureLoading
    };
};

const getCaptureDimensions = (
    captureContainer: HTMLDivElement,
    captureFixedHeaderContainer: HTMLDivElement,
    captureScrollContainer: HTMLDivElement
) => {
    const fixedHeaderStyle = window.getComputedStyle(captureFixedHeaderContainer);
    const headerTopPadding = parseFloat(fixedHeaderStyle.paddingTop);
    const headerSidePadding = Math.max(
        parseFloat(fixedHeaderStyle.paddingLeft),
        parseFloat(fixedHeaderStyle.paddingRight)
    );

    const scrollContainerStyle = window.getComputedStyle(captureScrollContainer);
    const scrollBottomPadding = parseFloat(scrollContainerStyle.paddingBottom);
    const scrollSidePadding = Math.max(
        parseFloat(scrollContainerStyle.paddingLeft),
        parseFloat(scrollContainerStyle.paddingRight)
    );

    const containerWidth = captureContainer.scrollWidth;
    const containerHeight = captureContainer.scrollHeight;
    const containerSidePadding = Math.max(headerSidePadding, scrollSidePadding);

    const captureWidth =
        containerWidth + (CAPTURE_PADDING > containerSidePadding ? (CAPTURE_PADDING - containerSidePadding) * 2 : 0);
    const captureHeight =
        containerHeight +
        (CAPTURE_PADDING > headerTopPadding ? CAPTURE_PADDING - headerTopPadding : 0) +
        (CAPTURE_PADDING > scrollBottomPadding ? CAPTURE_PADDING - scrollBottomPadding : 0);

    const captureTopPadding =
        CAPTURE_PADDING > headerTopPadding ? CAPTURE_PADDING - headerTopPadding : headerTopPadding;
    const captureSidePadding =
        CAPTURE_PADDING > containerSidePadding ? CAPTURE_PADDING - containerSidePadding : containerSidePadding;
    const captureBottomPadding =
        CAPTURE_PADDING > scrollBottomPadding ? CAPTURE_PADDING - scrollBottomPadding : scrollBottomPadding;

    return {
        captureWidth,
        captureHeight,
        captureTopPadding,
        captureSidePadding,
        captureBottomPadding
    };
};

export default useUniversalMfaDashboardToolbarActions;
